import { gql } from './GraphqlClient';
import { expectList, expectRecord } from './answerShape';

/**
 * I-1988: why a work area below refuses a deletion, as data: the template keys, the work areas below
 * the reader can read, and how many more there are.
 */
export interface IHiddenFieldsBelow {
  keys: string[];
  workAreas: string[];
  unseen: number;
}

/**
 * A namespace as the web app reads it (F-0221). Mirrors `NamespaceGql` on the backend
 * (`backend/src/namespaces/namespaces.types.ts`).
 */
export interface INamespace {
  id: string;
  /** The handle. A namespace nobody gave a custom handle has its key here (ADR-113). */
  path: string;
  /** The immutable key (I-0681): `<key>/<slug>` names a work area whatever the handle becomes. */
  key: string;
  /** `'user'` or `'group'`. A group with no `parentId` is an organisation; with one, a subgroup. */
  kind: string;
  /** Never empty since I-0657: a namespace starts with its handle as its name. */
  name: string;
  parentId: string | null;
  ownerUserId: string | null;
  /** The `/`-joined path from the root — `acme`, `acme/platform`. */
  fullPath: string;
  createdAt: string;
  /**
   * Whether the caller may create a subgroup or a work area directly here (I-1054, §10.13): `true`
   * unconditionally for a personal namespace, `work_area.manage` at this namespace for an
   * organisation or subgroup — the same permission the server's own refusal checks, batched across
   * the whole tree in one call rather than one request per row.
   */
  canManageHere: boolean;
  /**
   * The names of the roles the caller holds here, bound here or above (I-1679, §10.13 CA-6): what a
   * refused create names (`namespaceCreateRefusal`). Absent from a server older than the field.
   */
  rolesHere?: string[];
  /**
   * I-2092: whether signing out removes a person's data from the device. Set on an organisation by
   * its Owner; absent from a server older than the field, which requires nothing.
   */
  signOutRemovesDeviceData?: boolean;
  /**
   * F-0745: when the organisation's logo was last set, or null without one; the picture is
   * `organisationLogoApi.picture`. Absent from a server older than the field.
   */
  logoUpdatedAt?: string | null;
}

/** Whether a handle may be taken, answered while a person types (I-0657, AC-M9). */
export interface INamespacePathAvailability {
  /** `available`, `taken`, `reserved` or `malformed`. */
  status: string;
  reason: string | null;
  ground: string | null;
}

export interface IMoveWorkAreaToNamespaceResult {
  workAreaId: string;
  namespaceId: string;
  namespacePath: string;
  namespaceKey: string;
}

/** Whether this person may create an organisation now, and by which rule if not (AC-O10). */
export interface IOrganisationCreationAllowance {
  allowed: boolean;
  /** One of `ORGANISATION_CREATION_REFUSALS`, or null. */
  refusal: string | null;
  message: string | null;
  administrator: boolean;
  /** How many unused organisations this person owns: what the limit counts (D-0078). */
  ownedUnused: number;
  /** Every organisation this person owns. */
  ownedOrganisationIds: string[];
  /** Which of them are not in use yet (AC-O11). */
  unusedOrganisationIds: string[];
  audience: string;
  unusedPerPersonLimit: number | null;
}

/** A person or an organisation, as an administrator judging a reclaim sees it (AC-O7). */
export interface INamespaceAdministrationEntry {
  id: string;
  path: string;
  key: string;
  kind: string;
  name: string;
  createdAt: string;
  workAreas: number;
  lastActivityAt: string | null;
  /** For an organisation, whether it is in use (AC-O11); null for a person. */
  inUse: boolean | null;
  /** Unused for 90 days or more: eligible for reclaim (AC-O12). */
  reclaimEligible: boolean;
  /** F-0529: a demo organisation — billing and usage skip it. */
  demo: boolean;
}

export type NamespaceReleaseReasonType = 'inactive' | 'rights-holder' | 'other';

/** What the organisation flow sends (I-0682). */
export interface ICreateOrganisationRequest {
  /** A custom handle, or null for the generated key. */
  path: string | null;
  name: string;
  /** Only an administrator may name somebody else. */
  ownerEmail?: string | null;
}

/**
 * What deleting an organisation would do, and whether the reader may ask for it (I-0971,
 * `concept-account-deletion-and-export.md` §4a). Mirrors `OrganisationDeletionPreviewGql`.
 */
export interface IOrganisationDeletionPreview {
  id: string;
  name: string;
  path: string;
  workAreas: Array<{ id: string; name: string; slug: string }>;
  subgroups: number;
  members: number;
  graceDays: number;
  liveSubscription: boolean;
  /** F-0683 MP-15: machines enrolled for the organisation and never removed, each removed first. */
  machines: number;
  /** I-2444: their names, so the refusal names each; absent from an older deployment. */
  machineLabels?: string[];
  /** I-2444: removed machines whose records are deleted with the organisation. */
  removedMachines?: number;
  /** I-1988: why a work area below it, holding a value its templates hide, refuses the deletion. */
  hiddenFieldsRefusal: string | null;
  /** The same refusal as data, worded in the reader's language (`hiddenFieldsBelowText`). */
  hiddenFields?: IHiddenFieldsBelow | null;
  pendingRequestedAt: string | null;
  pendingScheduledFor: string | null;
  mayDelete: boolean;
  refusal: string | null;
}

export type OrganisationDeletionResultType =
  | { status: 'scheduled'; scheduledFor: string }
  | { status: 'deleted'; scheduledFor: null };

/**
 * An organisation the caller directly owns, whose deletion is pending (I-1002, §4a's amendment):
 * what Settings shows, since the organisation answers as deleted everywhere else. Mirrors
 * `OrganisationPendingDeletionGql`.
 */
export interface IOrganisationPendingDeletion {
  id: string;
  name: string;
  path: string;
  requestedAt: string;
  scheduledFor: string;
  liveSubscription: boolean;
}

const NAMESPACE_FIELDS = `
  id
  path
  key
  kind
  name
  parentId
  ownerUserId
  fullPath
  createdAt
  canManageHere
  rolesHere
  signOutRemovesDeviceData
  logoUpdatedAt
`;

export const namespaceApi = {
  async tree(): Promise<INamespace[]> {
    const data = await gql<{ namespaceTree: INamespace[] }>(
      `query NamespaceTree {
        namespaceTree { ${NAMESPACE_FIELDS} }
      }`,
    );
    return data.namespaceTree;
  },

  /** Only the settings flow calls this (I-0682, AC-O1). */
  async createOrganisation(request: ICreateOrganisationRequest): Promise<INamespace> {
    const data = await gql<{ createOrganisation: INamespace }>(
      `mutation CreateOrganisation($input: CreateOrganisationInput!) {
        createOrganisation(input: $input) { ${NAMESPACE_FIELDS} }
      }`,
      {
        input: {
          path: request.path,
          name: request.name,
          ownerEmail: request.ownerEmail ?? null,
        },
      },
    );
    return data.createOrganisation;
  },

  async organisationCreationAllowance(): Promise<IOrganisationCreationAllowance> {
    const data = await gql<{ organisationCreationAllowance: IOrganisationCreationAllowance }>(
      `query OrganisationCreationAllowance {
        organisationCreationAllowance {
          allowed
          refusal
          message
          administrator
          ownedUnused
          ownedOrganisationIds
          unusedOrganisationIds
          audience
          unusedPerPersonLimit
        }
      }`,
    );
    const allowance = expectRecord<IOrganisationCreationAllowance>(
      data.organisationCreationAllowance,
      'Organisation allowance',
      ['allowed', 'ownedOrganisationIds'],
    );
    expectList(allowance.ownedOrganisationIds, 'Organisation allowance');
    return allowance;
  },

  async administrationList(): Promise<INamespaceAdministrationEntry[]> {
    const data = await gql<{ namespacesForAdministration: INamespaceAdministrationEntry[] }>(
      `query NamespacesForAdministration {
        namespacesForAdministration {
          id
          path
          key
          kind
          name
          createdAt
          workAreas
          lastActivityAt
          inUse
          reclaimEligible
          demo
        }
      }`,
    );
    return expectList<INamespaceAdministrationEntry>(
      data.namespacesForAdministration,
      'Organisations and handles',
    );
  },

  /** An administrator releases a handle (I-0682, AC-O7). */
  async releaseHandle(
    id: string,
    reason: NamespaceReleaseReasonType,
    note: string | null,
  ): Promise<INamespace> {
    const data = await gql<{ releaseNamespaceHandle: INamespace }>(
      `mutation ReleaseNamespaceHandle($input: ReleaseNamespaceHandleInput!) {
        releaseNamespaceHandle(input: $input) { ${NAMESPACE_FIELDS} }
      }`,
      { input: { id, reason, note } },
    );
    return data.releaseNamespaceHandle;
  },

  async createSubgroup(parentId: string, path: string, name?: string | null): Promise<INamespace> {
    const data = await gql<{ createSubgroup: INamespace }>(
      `mutation CreateSubgroup($input: CreateSubgroupInput!) {
        createSubgroup(input: $input) { ${NAMESPACE_FIELDS} }
      }`,
      { input: { parentId, path, name: name ?? null } },
    );
    return data.createSubgroup;
  },

  /** Change a namespace's handle, its name, or both (I-0657, AC-M7, ADR-111). */
  async rename(
    id: string,
    change: { path?: string | null; name?: string | null },
  ): Promise<INamespace> {
    const data = await gql<{ renameNamespace: INamespace }>(
      `mutation RenameNamespace($input: RenameNamespaceInput!) {
        renameNamespace(input: $input) { ${NAMESPACE_FIELDS} }
      }`,
      { input: { id, path: change.path ?? null, name: change.name ?? null } },
    );
    return data.renameNamespace;
  },

  /** Require, or stop requiring, that signing out removes a person's data from the device (I-2092). */
  async setSignOutRemovesDeviceData(id: string, required: boolean): Promise<INamespace> {
    const data = await gql<{ setSignOutRemovesDeviceData: INamespace }>(
      `mutation SetSignOutRemovesDeviceData($input: SetSignOutRemovesDeviceDataInput!) {
        setSignOutRemovesDeviceData(input: $input) { ${NAMESPACE_FIELDS} }
      }`,
      { input: { id, required } },
    );
    return data.setSignOutRemovesDeviceData;
  },

  /**
   * Hand an organisation's ownership to another of its members, in one atomic server act (F-0412,
   * concept §10.13 TO-6) — `transferNamespaceOwnership`, not two `accessControlApi.change` calls.
   */
  async transferOwnership(id: string, newOwnerUserId: string): Promise<INamespace> {
    const data = await gql<{ transferNamespaceOwnership: INamespace }>(
      `mutation TransferNamespaceOwnership($input: TransferNamespaceOwnershipInput!) {
        transferNamespaceOwnership(input: $input) { ${NAMESPACE_FIELDS} }
      }`,
      { input: { id, newOwnerUserId } },
    );
    return data.transferNamespaceOwnership;
  },

  async pathAvailability(
    path: string,
    parentId: string | null,
    exceptNamespaceId: string | null,
  ): Promise<INamespacePathAvailability> {
    const data = await gql<{ namespacePathAvailability: INamespacePathAvailability }>(
      `query NamespacePathAvailability($path: String!, $parentId: ID, $exceptNamespaceId: ID) {
        namespacePathAvailability(path: $path, parentId: $parentId, exceptNamespaceId: $exceptNamespaceId) {
          status
          reason
          ground
        }
      }`,
      { path, parentId, exceptNamespaceId },
    );
    return data.namespacePathAvailability;
  },

  async moveWorkAreaToNamespace(
    workAreaId: string,
    namespaceId: string,
  ): Promise<IMoveWorkAreaToNamespaceResult> {
    const data = await gql<{ moveWorkAreaToNamespace: IMoveWorkAreaToNamespaceResult }>(
      `mutation MoveWorkAreaToNamespace($workAreaId: ID!, $namespaceId: ID!) {
        moveWorkAreaToNamespace(workAreaId: $workAreaId, namespaceId: $namespaceId) {
          workAreaId
          namespaceId
          namespacePath
          namespaceKey
        }
      }`,
      { workAreaId, namespaceId },
    );
    return data.moveWorkAreaToNamespace;
  },

  /** What deleting an organisation would do (I-0971, §4a). */
  async organisationDeletion(id: string): Promise<IOrganisationDeletionPreview> {
    const data = await gql<{ organisationDeletion: IOrganisationDeletionPreview }>(
      `query OrganisationDeletion($id: ID!) {
        organisationDeletion(id: $id) {
          id
          name
          path
          workAreas { id name slug }
          subgroups
          members
          graceDays
          liveSubscription
          machines
          machineLabels
          removedMachines
          hiddenFieldsRefusal
          hiddenFields {
            keys
            workAreas
            unseen
          }
          pendingRequestedAt
          pendingScheduledFor
          mayDelete
          refusal
        }
      }`,
      { id },
    );
    return data.organisationDeletion;
  },

  /** Ask for an organisation's deletion, with its handle typed (§4a). */
  async requestOrganisationDeletion(
    id: string,
    confirmHandle: string,
  ): Promise<OrganisationDeletionResultType> {
    const data = await gql<{ requestOrganisationDeletion: OrganisationDeletionResultType }>(
      `mutation RequestOrganisationDeletion($input: RequestOrganisationDeletionInput!) {
        requestOrganisationDeletion(input: $input) { status scheduledFor }
      }`,
      { input: { id, confirmHandle } },
    );
    return data.requestOrganisationDeletion;
  },

  /** Withdraw a pending deletion (§4a). */
  async cancelOrganisationDeletion(id: string): Promise<boolean> {
    const data = await gql<{ cancelOrganisationDeletion: boolean }>(
      `mutation CancelOrganisationDeletion($id: ID!) {
        cancelOrganisationDeletion(id: $id)
      }`,
      { id },
    );
    return data.cancelOrganisationDeletion;
  },

  /**
   * Every organisation the caller owns whose deletion is pending (I-1002, §4a's amendment): what
   * Settings → Delete account shows, since the organisation itself behaves as deleted everywhere else.
   */
  async myPendingOrganisationDeletions(): Promise<IOrganisationPendingDeletion[]> {
    const data = await gql<{ myPendingOrganisationDeletions: IOrganisationPendingDeletion[] }>(
      `query MyPendingOrganisationDeletions {
        myPendingOrganisationDeletions {
          id
          name
          path
          requestedAt
          scheduledFor
          liveSubscription
        }
      }`,
    );
    return expectList<IOrganisationPendingDeletion>(
      data.myPendingOrganisationDeletions,
      'Organisations being deleted',
    );
  },
};
